import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@monrdv/shared';
import { ApiError, request } from '../lib/api.js';
import { useEditGuard } from '../lib/liveEditing.js';

/**
 * Correcting a customer.
 *
 * Separate from the customer's file, which is a dense read-only summary of a whole
 * relationship — 1 049 lines of it — and shows only the *primary* phone, email and address.
 * Editing needs the opposite: every row, room to type, and one visual language shared with the
 * creation form.
 *
 * **Two save models on one screen, deliberately.** The identity fields are one record and save
 * together, behind a button. The phones, emails and addresses are separate rows behind separate
 * endpoints, and each acts the moment you ask — because "remove this dead phone number" should
 * not be something you can lose by navigating away, and because a list of things is not a form
 * field. The sections are titled so the difference is visible rather than surprising.
 *
 * **This is the first screen to hold the edit lock.** `useEditGuard` and `ReconcileDialog` were
 * written before anything could edit, and `realtime.ts` has been holding invalidations for a
 * dirty record ever since — for a lock nothing ever took. This takes it: while the identity
 * form has unsaved changes, an incoming change to this customer is held rather than applied,
 * and the notice below offers to load it. Nothing is overwritten silently in either direction.
 */

type Editable = {
  id: string;
  number: string | null;
  firstName: string | null;
  lastName: string | null;
  organization: string | null;
  displayName: string;
  locale: Locale;
  status: 'active' | 'inactive' | 'archived';
  note: string | null;
  taxable: boolean;
  billToParent: boolean;
  parentId: string | null;
  phones: Array<{ id: string; kind: string; raw: string; extension: string | null; isPrimary: boolean; smsConsent: boolean }>;
  emails: Array<{ id: string; kind: string; email: string; isPrimary: boolean; bounced: boolean }>;
  addresses: Array<{
    id: string;
    kind: string;
    label: string | null;
    line1: string;
    line2: string | null;
    city: string;
    region: string;
    postalCode: string | null;
    note: string | null;
    isPrimary: boolean;
  }>;
};

type Identity = {
  firstName: string;
  lastName: string;
  organization: string;
  locale: Locale;
  status: 'active' | 'inactive' | 'archived';
  note: string;
  taxable: boolean;
};

const PHONE_KINDS = ['mobile', 'home', 'work', 'main', 'other'] as const;
const EMAIL_KINDS = ['main', 'personal', 'professional', 'billing', 'other'] as const;
const ADDRESS_KINDS = ['service', 'billing', 'shipping', 'main'] as const;

const identityOf = (c: Editable): Identity => ({
  firstName: c.firstName ?? '',
  lastName: c.lastName ?? '',
  organization: c.organization ?? '',
  locale: c.locale,
  status: c.status,
  note: c.note ?? '',
  taxable: c.taxable,
});

/** An empty box means "no value", which is `null` on the wire, not `''`. */
const orNull = (value: string) => (value.trim() ? value.trim() : null);

const hasDigits = (value: string) => /\d/.test(value);

export function CustomerEditScreen() {
  const { t: translate } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const { customerId } = useParams({ strict: false }) as { customerId: string };
  const navigate = useNavigate();
  const client = useQueryClient();

  const customer = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => request<Editable>(`/customers/${customerId}`),
  });

  const [identity, setIdentity] = useState<Identity | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seed the form once the record arrives, and again whenever a reload is accepted.
  useEffect(() => {
    if (customer.data && !identity) setIdentity(identityOf(customer.data));
  }, [customer.data, identity]);

  const server = customer.data ? identityOf(customer.data) : null;
  const dirty = !!identity && !!server && JSON.stringify(identity) !== JSON.stringify(server);

  /*
   * Hold the lock only while there is something to lose. Marking the record dirty for as long
   * as this screen is open would freeze every colleague's view of it behind an editor that may
   * just be reading.
   */
  const guard = useEditGuard('customer', customerId, dirty);

  const named = !!identity && !!(identity.firstName.trim() || identity.lastName.trim() || identity.organization.trim());

  const reload = async () => {
    guard.accept();
    setIdentity(null);
    await client.invalidateQueries({ queryKey: ['customer', customerId] });
  };

  const invalidate = () => {
    void client.invalidateQueries({ queryKey: ['customer', customerId] });
    void client.invalidateQueries({ queryKey: ['customers'] });
    void client.invalidateQueries({ queryKey: ['search'] });
  };

  const fail = (err: unknown, fallback: string) =>
    setError(err instanceof ApiError && err.message ? err.message : t(fallback));

  const saveIdentity = useMutation({
    mutationFn: () => {
      if (!identity || !server) throw new Error('nothing to save');
      // Only what actually differs. The endpoint is `.strict()` and records an audit row per
      // changed field, so resending the whole form would write a dozen no-op changes into a
      // trail somebody has to read.
      const patch: Record<string, unknown> = {};
      if (identity.firstName !== server.firstName) patch.firstName = orNull(identity.firstName);
      if (identity.lastName !== server.lastName) patch.lastName = orNull(identity.lastName);
      if (identity.organization !== server.organization) patch.organization = orNull(identity.organization);
      if (identity.locale !== server.locale) patch.locale = identity.locale;
      if (identity.status !== server.status) patch.status = identity.status;
      if (identity.note !== server.note) patch.note = orNull(identity.note);
      if (identity.taxable !== server.taxable) patch.taxable = identity.taxable;
      // The endpoint answers with the whole record, deliberately — see `customers.ts`. It is
      // discarded here in favour of refetching, because the display name is recomputed by
      // trigger and the canonical GET is the one shape this screen already reads.
      return request<Editable>(`/customers/${customerId}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
    },
    onSuccess: () => {
      // Reseed from the server rather than trusting the local draft: the display name is
      // recomputed by trigger, so what was saved is not exactly what was sent.
      setIdentity(null);
      invalidate();
    },
    onError: (err) => fail(err, 'customer.form.failed'),
  });

  if (customer.isPending) return <p className="recform">{t('common.loading')}</p>;
  if (customer.isError || !customer.data || !identity) {
    return (
      <div className="recform">
        <p className="recform__error" role="alert">
          {t('customer.edit.gone')}
        </p>
        <Link to="/customers" className="btn btn--ghost">
          {t('common.back')}
        </Link>
      </div>
    );
  }

  const c = customer.data;
  const busy = saveIdentity.isPending;

  return (
    <div className="recform">
      <header className="recform__head">
        <Link to="/customers/$customerId" params={{ customerId }} className="screen__breadcrumb">
          {t('common.back')} · {c.displayName}
        </Link>
        <h1 className="recform__title">{t('customer.edit.title')}</h1>
        <p className="recform__lede">{t('customer.edit.lede')}</p>
      </header>

      {/*
        Somebody else saved this customer while the form has been open. Offered, never applied:
        accepting discards what is typed here, so it is the editor's decision.
      */}
      {guard.supersededBy > 0 && (
        <p className="recform__notice" role="status">
          {t('customer.edit.superseded')}{' '}
          <button type="button" className="recform__add" onClick={() => void reload()}>
            {t('customer.edit.reload')}
          </button>
        </p>
      )}

      {error && (
        <p className="recform__error" role="alert">
          {error}
        </p>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          if (named && dirty) saveIdentity.mutate();
        }}
        noValidate
      >
        <fieldset className="recform__section" disabled={busy}>
          <legend className="recform__legend">{t('customer.form.identity')}</legend>

          <div className="recform__pair">
            <div className="recform__field">
              <label className="recform__label" htmlFor="firstName">
                {t('customer.form.firstName')}
              </label>
              <input
                id="firstName"
                className="recform__input"
                value={identity.firstName}
                onChange={(e) => setIdentity({ ...identity, firstName: e.target.value })}
                autoComplete="off"
              />
            </div>
            <div className="recform__field">
              <label className="recform__label" htmlFor="lastName">
                {t('customer.form.lastName')}
              </label>
              <input
                id="lastName"
                className="recform__input"
                value={identity.lastName}
                onChange={(e) => setIdentity({ ...identity, lastName: e.target.value })}
                autoComplete="off"
              />
            </div>
          </div>

          <div className="recform__field">
            <label className="recform__label" htmlFor="organization">
              {t('customer.form.organization')}
            </label>
            <input
              id="organization"
              className="recform__input"
              value={identity.organization}
              onChange={(e) => setIdentity({ ...identity, organization: e.target.value })}
              autoComplete="off"
            />
            {!named && <span className="recform__hint">{t('customer.form.nameRule')}</span>}
          </div>

          <div className="recform__pair">
            <div className="recform__field">
              <label className="recform__label" htmlFor="locale">
                {t('customer.form.locale')}
              </label>
              <select
                id="locale"
                className="recform__select"
                value={identity.locale}
                onChange={(e) => setIdentity({ ...identity, locale: e.target.value as Locale })}
              >
                <option value="fr">{t('customer.form.localeFr')}</option>
                <option value="en">{t('customer.form.localeEn')}</option>
              </select>
            </div>
            <div className="recform__field">
              <label className="recform__label" htmlFor="status">
                {t('common.status')}
              </label>
              <select
                id="status"
                className="recform__select"
                value={identity.status}
                onChange={(e) => setIdentity({ ...identity, status: e.target.value as Identity['status'] })}
              >
                <option value="active">{t('customer.edit.statusActive')}</option>
                <option value="inactive">{t('customer.edit.statusInactive')}</option>
                <option value="archived">{t('customer.edit.statusArchived')}</option>
              </select>
              {/* Archiving is a status, not a deletion: 3 268 jobs reference these rows. */}
              <span className="recform__hint">{t('customer.edit.statusHint')}</span>
            </div>
          </div>

          <label className="recform__check">
            <input
              type="checkbox"
              checked={identity.taxable}
              onChange={(e) => setIdentity({ ...identity, taxable: e.target.checked })}
            />
            <span>{t('customer.form.taxable')}</span>
          </label>

          <div className="recform__field">
            <label className="recform__label" htmlFor="note">
              {t('customer.internalNote')}
            </label>
            <textarea
              id="note"
              className="recform__textarea"
              value={identity.note}
              onChange={(e) => setIdentity({ ...identity, note: e.target.value })}
            />
            <span className="recform__hint">{t('customer.form.noteHint')}</span>
          </div>
        </fieldset>

        <div className="recform__actions">
          <button className="btn btn--primary" type="submit" disabled={busy || !dirty || !named}>
            {busy ? t('customer.form.saving') : t('common.save')}
          </button>
          <button
            className="btn btn--ghost"
            type="button"
            disabled={busy || !dirty}
            onClick={() => setIdentity(server)}
          >
            {t('customer.edit.revert')}
          </button>
          <span className="recform__spacer" />
        </div>
      </form>

      {/*
        Below the save button on purpose: everything from here down acts immediately, and
        putting it above would imply it waits for the same button.
      */}
      <ContactRows
        t={t}
        customerId={customerId}
        phones={c.phones}
        emails={c.emails}
        onChanged={invalidate}
        onError={fail}
      />

      <Addresses t={t} customerId={customerId} addresses={c.addresses} onChanged={invalidate} onError={fail} />

      <div className="recform__actions">
        <button className="btn btn--ghost" type="button" onClick={() => void navigate({ to: '/customers/$customerId', params: { customerId } })}>
          {t('customer.edit.done')}
        </button>
        <span className="recform__spacer" />
      </div>
    </div>
  );
}

/** Phones and emails: two lists, one shape, each row acting on its own. */
function ContactRows({
  t,
  customerId,
  phones,
  emails,
  onChanged,
  onError,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  customerId: string;
  phones: Editable['phones'];
  emails: Editable['emails'];
  onChanged: () => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const [newPhone, setNewPhone] = useState({ kind: 'mobile', raw: '', extension: '' });
  const [newEmail, setNewEmail] = useState({ kind: 'main', email: '' });

  const addPhone = useMutation({
    mutationFn: () =>
      request(`/customers/${customerId}/phones`, {
        method: 'POST',
        body: JSON.stringify({
          kind: newPhone.kind,
          raw: newPhone.raw.trim(),
          extension: newPhone.extension.trim() || undefined,
        }),
      }),
    onSuccess: () => {
      setNewPhone({ kind: 'mobile', raw: '', extension: '' });
      onChanged();
    },
    onError: (err) => onError(err, 'customer.edit.phoneFailed'),
  });

  const removePhone = useMutation({
    mutationFn: (id: string) => request(`/customers/${customerId}/phones/${id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
    onError: (err) => onError(err, 'customer.edit.phoneFailed'),
  });

  const addEmail = useMutation({
    mutationFn: () =>
      request(`/customers/${customerId}/emails`, {
        method: 'POST',
        body: JSON.stringify({ kind: newEmail.kind, email: newEmail.email.trim() }),
      }),
    onSuccess: () => {
      setNewEmail({ kind: 'main', email: '' });
      onChanged();
    },
    onError: (err) => onError(err, 'customer.edit.emailFailed'),
  });

  const removeEmail = useMutation({
    mutationFn: (id: string) => request(`/customers/${customerId}/emails/${id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
    onError: (err) => onError(err, 'customer.edit.emailFailed'),
  });

  const phoneReady = !!newPhone.raw.trim() && hasDigits(newPhone.raw);

  return (
    <fieldset className="recform__section">
      <legend className="recform__legend">{t('customer.edit.contactNow')}</legend>

      <div className="recform__field">
        <span className="recform__label">{t('customer.phone')}</span>
        <div className="recform__repeat">
          {phones.map((phone) => (
            <div className="recform__row" key={phone.id}>
              <span className="recform__hint">{t(`customer.form.kind.${phone.kind}`, { defaultValue: phone.kind })}</span>
              <span className="phone">
                {phone.raw}
                {phone.extension ? ` p. ${phone.extension}` : ''}
                {phone.isPrimary ? ` · ${t('customer.edit.primary')}` : ''}
              </span>
              <RemoveButton
                label={t('customer.form.removePhone')}
                disabled={removePhone.isPending}
                onClick={() => removePhone.mutate(phone.id)}
              />
            </div>
          ))}
          {!phones.length && <p className="recform__hint">{t('common.none')}</p>}

          <div className="recform__row">
            <select
              className="recform__select"
              aria-label={t('customer.form.phoneKind')}
              value={newPhone.kind}
              onChange={(e) => setNewPhone({ ...newPhone, kind: e.target.value })}
            >
              {PHONE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`customer.form.kind.${kind}`)}
                </option>
              ))}
            </select>
            <input
              className="recform__input recform__input--tel"
              aria-label={t('customer.form.addPhone')}
              inputMode="tel"
              autoComplete="off"
              placeholder={t('customer.form.phonePlaceholder')}
              value={newPhone.raw}
              onChange={(e) => setNewPhone({ ...newPhone, raw: e.target.value })}
            />
            <button
              type="button"
              className="btn btn--ghost"
              disabled={!phoneReady || addPhone.isPending}
              onClick={() => addPhone.mutate()}
            >
              {t('customer.edit.add')}
            </button>
          </div>
        </div>
      </div>

      <div className="recform__field">
        <span className="recform__label">{t('customer.email')}</span>
        <div className="recform__repeat">
          {emails.map((entry) => (
            <div className="recform__row" key={entry.id}>
              <span className="recform__hint">{t(`customer.form.kind.${entry.kind}`, { defaultValue: entry.kind })}</span>
              <span>
                {entry.email}
                {entry.bounced ? ` · ${t('customer.bounced')}` : ''}
                {entry.isPrimary ? ` · ${t('customer.edit.primary')}` : ''}
              </span>
              <RemoveButton
                label={t('customer.form.removeEmail')}
                disabled={removeEmail.isPending}
                onClick={() => removeEmail.mutate(entry.id)}
              />
            </div>
          ))}
          {!emails.length && <p className="recform__hint">{t('common.none')}</p>}

          <div className="recform__row">
            <select
              className="recform__select"
              aria-label={t('customer.form.emailKind')}
              value={newEmail.kind}
              onChange={(e) => setNewEmail({ ...newEmail, kind: e.target.value })}
            >
              {EMAIL_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`customer.form.kind.${kind}`)}
                </option>
              ))}
            </select>
            <input
              className="recform__input"
              aria-label={t('customer.form.addEmail')}
              type="email"
              inputMode="email"
              autoComplete="off"
              placeholder={t('customer.form.emailPlaceholder')}
              value={newEmail.email}
              onChange={(e) => setNewEmail({ ...newEmail, email: e.target.value })}
            />
            <button
              type="button"
              className="btn btn--ghost"
              disabled={!newEmail.email.trim() || addEmail.isPending}
              onClick={() => addEmail.mutate()}
            >
              {t('customer.edit.add')}
            </button>
          </div>
        </div>
      </div>
    </fieldset>
  );
}

/**
 * Addresses, each saved on its own.
 *
 * The access note is the field that actually gets corrected — a gate code changes, the parking
 * moves — so the fields are open rather than hidden behind a per-row "edit" toggle nobody would
 * find. A row with nothing changed sends nothing: the endpoint answers `changed: []`.
 */
function Addresses({
  t,
  customerId,
  addresses,
  onChanged,
  onError,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  customerId: string;
  addresses: Editable['addresses'];
  onChanged: () => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  return (
    <fieldset className="recform__section">
      <legend className="recform__legend">{t('customer.edit.addressesNow')}</legend>

      {addresses.map((address) => (
        <AddressRow key={address.id} t={t} customerId={customerId} address={address} onChanged={onChanged} onError={onError} />
      ))}
      {!addresses.length && <p className="recform__hint">{t('common.none')}</p>}

      {adding ? (
        <AddressRow
          t={t}
          customerId={customerId}
          address={null}
          onChanged={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
          onError={onError}
        />
      ) : (
        <button type="button" className="recform__add" onClick={() => setAdding(true)}>
          {t('customer.edit.addAddress')}
        </button>
      )}
    </fieldset>
  );
}

type AddressDraft = { kind: string; line1: string; line2: string; city: string; region: string; postalCode: string; note: string };

function AddressRow({
  t,
  customerId,
  address,
  onChanged,
  onCancel,
  onError,
}: {
  t: (key: string, vars?: Record<string, string | number>) => string;
  customerId: string;
  address: Editable['addresses'][number] | null;
  onChanged: () => void;
  onCancel?: () => void;
  onError: (err: unknown, fallback: string) => void;
}) {
  const initial: AddressDraft = useMemo(
    () => ({
      kind: address?.kind ?? 'service',
      line1: address?.line1 ?? '',
      line2: address?.line2 ?? '',
      city: address?.city ?? '',
      region: address?.region ?? 'QC',
      postalCode: address?.postalCode ?? '',
      note: address?.note ?? '',
    }),
    [address],
  );
  const [draft, setDraft] = useState<AddressDraft>(initial);
  useEffect(() => setDraft(initial), [initial]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial);
  const complete = !!draft.line1.trim() && !!draft.city.trim();

  const save = useMutation({
    mutationFn: () => {
      const body: Record<string, unknown> = {};
      if (address) {
        // Only the fields that moved, so the audit row names the change and nothing else.
        if (draft.kind !== initial.kind) body.kind = draft.kind;
        if (draft.line1 !== initial.line1) body.line1 = draft.line1.trim();
        if (draft.line2 !== initial.line2) body.line2 = orNull(draft.line2);
        if (draft.city !== initial.city) body.city = draft.city.trim();
        if (draft.region !== initial.region) body.region = draft.region.trim();
        if (draft.postalCode !== initial.postalCode) body.postalCode = orNull(draft.postalCode);
        if (draft.note !== initial.note) body.note = orNull(draft.note);
        return request(`/customers/${customerId}/addresses/${address.id}`, {
          method: 'PATCH',
          body: JSON.stringify(body),
        });
      }
      return request(`/customers/${customerId}/addresses`, {
        method: 'POST',
        body: JSON.stringify({
          kind: draft.kind,
          line1: draft.line1.trim(),
          line2: orNull(draft.line2) ?? undefined,
          city: draft.city.trim(),
          region: draft.region.trim() || 'QC',
          postalCode: orNull(draft.postalCode) ?? undefined,
          note: orNull(draft.note) ?? undefined,
        }),
      });
    },
    onSuccess: onChanged,
    onError: (err) => onError(err, 'customer.edit.addressFailed'),
  });

  const remove = useMutation({
    mutationFn: () => request(`/customers/${customerId}/addresses/${address!.id}`, { method: 'DELETE' }),
    onSuccess: onChanged,
    // A 409 here is the API refusing to strand a job's history. Its message says so.
    onError: (err) => onError(err, 'customer.edit.addressFailed'),
  });

  const busy = save.isPending || remove.isPending;

  return (
    <div className="recform__field">
      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label">{t('customer.form.line1')}</label>
          <input
            className="recform__input"
            value={draft.line1}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, line1: e.target.value })}
            placeholder={t('customer.form.line1Placeholder')}
          />
        </div>
        <div className="recform__field">
          <label className="recform__label">{t('customer.city')}</label>
          <input
            className="recform__input"
            value={draft.city}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
          />
        </div>
      </div>

      <div className="recform__pair">
        <div className="recform__field">
          <label className="recform__label">{t('customer.form.postalCode')}</label>
          <input
            className="recform__input"
            value={draft.postalCode}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })}
          />
        </div>
        <div className="recform__field">
          <label className="recform__label">{t('customer.form.addressKind')}</label>
          <select
            className="recform__select"
            value={draft.kind}
            disabled={busy}
            onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
          >
            {ADDRESS_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {t(`customer.edit.addressKinds.${kind}`)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="recform__field">
        <label className="recform__label">{t('customer.form.accessNote')}</label>
        <input
          className="recform__input"
          value={draft.note}
          disabled={busy}
          onChange={(e) => setDraft({ ...draft, note: e.target.value })}
          placeholder={t('customer.form.accessNotePlaceholder')}
        />
      </div>

      <div className="recform__row">
        <button
          type="button"
          className="btn btn--primary"
          disabled={busy || !complete || (!!address && !dirty)}
          onClick={() => save.mutate()}
        >
          {address ? t('common.save') : t('customer.edit.add')}
        </button>
        {address ? (
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => remove.mutate()}>
            {t('common.delete')}
          </button>
        ) : (
          <button type="button" className="btn btn--ghost" disabled={busy} onClick={onCancel}>
            {t('common.cancel')}
          </button>
        )}
        <span className="recform__spacer" />
      </div>
    </div>
  );
}

function RemoveButton({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) {
  return (
    <button type="button" className="recform__remove" aria-label={label} title={label} disabled={disabled} onClick={onClick}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2.5 6h7" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    </button>
  );
}
